import Sidebar from '../Sidebar'
import SoundEffectListFilters from './SoundEffectListFilters'
import type { SpecificSidebarProps } from '../Sidebar'
import { useMemo, type FC } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { fetchSoundEffects } from '@/features/sound-effect/domain/use-cases/fetch-sound-effects'
import SoundEffectBox from './SoundEffectBox'
import CardPlaceholder from '@/components/common/CardPlaceholder.tsx'

interface Store {
  searchTerm: string
  selectedTags: string[]
}

export const useStore = create<Store>()(
  persist(
    (): Store => ({
      searchTerm: '',
      selectedTags: [],
    }),
    {
      name: 'sound-effects-filters',
      partialize: (state) => ({
        searchTerm: state.searchTerm,
        selectedTags: state.selectedTags,
      }),
    }
  )
)
export const setSearchTerm = (searchTerm: string) => {
  useStore.setState({ searchTerm })
}

const SoundEffectsList: FC<SpecificSidebarProps> = ({ goBack, ...props }) => {
  const { searchTerm, selectedTags } = useStore()

  const { data, fetchNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['sound-effects-list', { tags: selectedTags }, searchTerm],
      queryFn: async ({ pageParam = 0 }) =>
        await fetchSoundEffects(pageParam, { tags: selectedTags }, searchTerm),
      getNextPageParam: (lastPage, pages) =>
        lastPage.page < lastPage.totalPages ? pages.length : undefined,
      initialPageParam: 0,
    })

  const { data: parametersData } = useQuery({
    queryKey: ['sound-effects-parameters', searchTerm],
    queryFn: () => fetchSoundEffects(0, { tags: [] }, searchTerm),
    staleTime: Infinity,
  })

  const soundEffects = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  )
  const tags = parametersData?.parameters?.tags ?? {}
  const noResults =
    !isLoading && !isFetchingNextPage && soundEffects.length === 0

  return (
    <Sidebar
      {...props}
      title="ls_sidebar_sound_effects_title"
      searchKey="ls_sidebar_sound_effects_search"
      searchAction={setSearchTerm}
      fetchMoreData={fetchNextPage}
      noResults={noResults}
      goBack={goBack}
    >
      <div className="flex flex-col gap-4 bg-zinc-900 p-4 pr-6">
        <SoundEffectListFilters
          tags={tags}
          onTagSelect={(tgs) => useStore.setState({ selectedTags: tgs })}
          selectedTags={selectedTags}
        />
        <div className="flex flex-col gap-2">
          {soundEffects.map((effect) => (
            <SoundEffectBox key={effect.id} effect={effect} />
          ))}
          {(isLoading || isFetchingNextPage) &&
            Array.from({ length: 15 }, (_, index) => (
              <CardPlaceholder key={index} height={96} />
            ))}
        </div>
      </div>
    </Sidebar>
  )
}

export default SoundEffectsList
